import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AdminAPI, HrStatusAPI } from "../api";
import { canShowHrApprove, canShowHrResubmit } from "../../config/joineeConstants";
import { JOINEE_FORM_STEPS } from "../../config/joineeFormConstants";
import NewJoinForm from "../NewJoineForm/NewJoinForm";
import Sdf from "../SelfDeclarationForm/Sdf";
import Epf from "../../EPF/Epf";
import Form11 from "../../Form11/Form11";
import FormF from "../../FormF/FormF";
import FormQ from "../../FormQ/FormQ";
import Nda from "../../NDA/Nda";
import { ADMIN_BUTTON_SX, ADMIN_COLORS, ADMIN_FONT, AdminPage, AdminSheet, AdminSheetHeader } from "./adminUi";
import EmployeeRecordPanel, { EmployeeRecordActions } from "./EmployeeRecordPanel";
import CommentDialog from "./CommentDialog";
import useJoineeDownloads from "./useJoineeDownloads";
import { JOINEE_FORMS, findJoineeForm, formStatusText } from "./joineeStatus";

var TOAST_OPTS = { hideProgressBar: true, closeOnClick: true };

/** Forms whose HR actions also wait for HR-entered data (employer certificate / wages). */
var GATED_FORMS = { formF: true, formQ: true };

var FORM_COMPONENTS = {
  newJoinee: NewJoinForm,
  sdf: Sdf,
  epf: Epf,
  form11: Form11,
  formF: FormF,
  formQ: FormQ,
  nda: Nda,
};

/** Default selection: first form waiting for HR, else the New Joinee Form. */
function initialFormId(details) {
  for (var i = 0; i < JOINEE_FORMS.length; i++) {
    var status = details[JOINEE_FORMS[i].key];
    if (status === "submitted" || status === "resubmitted") return JOINEE_FORMS[i].id;
  }
  return JOINEE_FORMS[0].id;
}

function SectionTabs(props) {
  return (
    <Box sx={{ p: "8px", mb: "14px", bgcolor: "#FFFFFF", border: "1px solid " + ADMIN_COLORS.line, borderRadius: "12px" }}>
    <Box
      role="tablist"
      aria-label="New Joinee Form sections"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))",
        gap: "6px",
        p: "6px",
        borderRadius: "8px",
        bgcolor: "#F1F2F4",
      }}
    >
      {JOINEE_FORM_STEPS.map(function (step, index) {
        var active = index === props.value;
        return (
          <ButtonBase
            key={step.id}
            role="tab"
            aria-selected={active}
            onClick={function () {
              props.onChange(index);
            }}
            sx={{
              height: 40,
              px: "8px",
              borderRadius: "6px",
              bgcolor: active ? ADMIN_COLORS.tabActive : "#FFFFFF",
              color: ADMIN_COLORS.label,
              fontFamily: ADMIN_FONT,
              fontSize: 16,
              lineHeight: "22px",
              fontWeight: active ? 600 : 500,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: active ? ADMIN_COLORS.tabActive : ADMIN_COLORS.brandSoft },
              "&.Mui-focusVisible": { outline: "2px solid " + ADMIN_COLORS.brand, outlineOffset: "-2px" },
            }}
          >
            {step.label}
          </ButtonBase>
        );
      })}
    </Box>
    </Box>
  );
}

/** HR review screen: record panel on the left, the selected form + Approve/Re-submit on the right. */
function EmployeeRecord() {
  var navigate = useNavigate();
  var params = useParams();
  var id = params.id || "";
  var [searchParams, setSearchParams] = useSearchParams();
  var downloads = useJoineeDownloads();

  var [details, setDetails] = useState(null);
  var [loading, setLoading] = useState(true);
  var [formVersion, setFormVersion] = useState(0);
  var [step, setStep] = useState(0);
  var [reviewReady, setReviewReady] = useState(false);
  var [commentMode, setCommentMode] = useState("");
  var [saving, setSaving] = useState(false);

  var requestedForm = findJoineeForm(searchParams.get("form"));
  var selectedForm = requestedForm || (details ? findJoineeForm(initialFormId(details)) : JOINEE_FORMS[0]);

  var loadDetails = useCallback(
    function () {
      return AdminAPI.getNewJoinee(id)
        .then(function (res) {
          if (!res || !res.success || !res.data) {
            throw new Error("not found");
          }
          setDetails(res.data);
          return res.data;
        })
        .catch(function (err) {
          console.log(err);
          toast.error("Unable to open this employee record", TOAST_OPTS);
          navigate("/AdminEmployees", { replace: true });
          return null;
        });
    },
    [id, navigate]
  );

  useEffect(
    function () {
      setLoading(true);
      loadDetails().finally(function () {
        setLoading(false);
      });
    },
    [loadDetails]
  );

  useEffect(
    function () {
      setReviewReady(false);
      setStep(0);
    },
    [selectedForm.id, formVersion]
  );

  function selectForm(formId) {
    setSearchParams({ form: formId }, { replace: true });
  }

  function handleToggleActive(nextActive) {
    AdminAPI.activeInactiveNewJoinee(id, nextActive ? "active" : "in-active")
      .then(function (res) {
        if (res.success) {
          toast.success(res.message, TOAST_OPTS);
          setDetails(function (prev) {
            return Object.assign({}, prev, { isActive: nextActive });
          });
        } else {
          toast.error(res.message, TOAST_OPTS);
        }
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to update status", TOAST_OPTS);
      });
  }

  var status = details ? details[selectedForm.key] : "";
  var ready = GATED_FORMS[selectedForm.id] ? reviewReady : true;
  var canApprove = Boolean(details) && canShowHrApprove(status) && ready;
  var canResubmit = Boolean(details) && canShowHrResubmit(status) && ready;

  function submitReview(comment) {
    var isApprove = commentMode === "approve";
    var nextStatus = isApprove ? "review done" : "resubmission required";
    var formIndex = JOINEE_FORMS.indexOf(selectedForm);
    setSaving(true);
    HrStatusAPI.hrFormStatus(id, nextStatus, selectedForm.key, {
      approvedComment: isApprove ? comment : "",
      reSubmitComment: isApprove ? "" : comment,
    })
      .then(function (res) {
        if (!res || !res.success) {
          throw new Error((res && res.message) || "failed");
        }
        return loadDetails();
      })
      .then(function (fresh) {
        if (!fresh) return;
        if (fresh[selectedForm.key] !== nextStatus) {
          toast.error(
            fresh.isActive
              ? "The form status could not be updated. Please try again."
              : "Turn on the candidate's login before approving or requesting changes.",
            TOAST_OPTS
          );
          return;
        }
        toast.success(
          selectedForm.label + (isApprove ? " approved" : " sent back for re-submission"),
          TOAST_OPTS
        );
        setCommentMode("");
        setFormVersion(function (v) {
          return v + 1;
        });
        var next = JOINEE_FORMS[formIndex + 1];
        if (next) selectForm(next.id);
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to update the form status", TOAST_OPTS);
      })
      .finally(function () {
        setSaving(false);
      });
  }

  var FormComponent = FORM_COMPONENTS[selectedForm.id];
  var isNewJoinee = selectedForm.id === "newJoinee";

  return (
    <AdminPage>
      <AdminSheet viewport>
        <AdminSheetHeader
          title="Employee Record"
          backLabel="Back to Manage New Joinee"
          onBack={function () {
            navigate("/AdminEmployees");
          }}
        />

        {loading || !details ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 10 }}>
            <CircularProgress size={28} sx={{ color: ADMIN_COLORS.brand }} />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", md: "row" } }}>
            <Box
              component="aside"
              sx={{
                width: { xs: "100%", md: 480, lg: 500 },
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                borderRight: { md: "1px solid " + ADMIN_COLORS.lineSoft },
                borderBottom: { xs: "1px solid " + ADMIN_COLORS.lineSoft, md: "none" },
                minHeight: 0,
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: "24px", pt: "20px", pb: "20px" }}>
                <EmployeeRecordPanel
                  details={details}
                  keyInfoDefaultOpen={false}
                  isActive={Boolean(details.isActive)}
                  onToggleActive={handleToggleActive}
                  selectedFormId={selectedForm.id}
                  onSelectForm={selectForm}
                />
              </Box>
              <Box sx={{ px: "24px", pb: "16px", flexShrink: 0 }}>
                <EmployeeRecordActions
                  onDownload={function () {
                    downloads.download(id);
                  }}
                  downloading={downloads.downloading}
                  onNotify={function () {
                    downloads.notify(id);
                  }}
                  notifying={downloads.notifying}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, minHeight: { xs: 480, md: 0 }, display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  px: { xs: "12px", md: "20px" },
                  py: "14px",
                  "& .joinee-form-shell": {
                    width: "100%",
                    maxWidth: "none",
                    height: "auto",
                    maxHeight: "none",
                  },
                }}
              >
                {isNewJoinee ? <SectionTabs value={step} onChange={setStep} /> : null}
                <FormComponent
                  key={selectedForm.id + "-" + formVersion}
                  id={id}
                  hideHrActions
                  step={isNewJoinee ? step : undefined}
                  onStepChange={isNewJoinee ? setStep : undefined}
                  onReviewReadyChange={GATED_FORMS[selectedForm.id] ? setReviewReady : undefined}
                />
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "10px",
                  p: "12px",
                  borderTop: "1px solid " + ADMIN_COLORS.lineSoft,
                  bgcolor: ADMIN_COLORS.card,
                }}
              >
                <Typography
                  sx={{ flex: 1, minWidth: 200, fontFamily: ADMIN_FONT, fontSize: 15, lineHeight: "20px", color: ADMIN_COLORS.labelSecondary }}
                >
                  {formStatusText(status, GATED_FORMS[selectedForm.id] && !ready && canShowHrResubmit(status))}
                </Typography>
                <Button
                  variant="outlined"
                  disabled={!canResubmit || saving}
                  onClick={function () {
                    setCommentMode("resubmit");
                  }}
                  sx={ADMIN_BUTTON_SX.outlined}
                >
                  Re-submit
                </Button>
                <Button
                  variant="contained"
                  disabled={!canApprove || saving}
                  onClick={function () {
                    setCommentMode("approve");
                  }}
                  sx={ADMIN_BUTTON_SX.contained}
                >
                  Approve
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </AdminSheet>

      <CommentDialog
        open={Boolean(commentMode)}
        busy={saving}
        submitLabel={commentMode === "approve" ? "Approve" : "Re-Submit"}
        onClose={function () {
          if (!saving) setCommentMode("");
        }}
        onSubmit={submitReview}
      />

      {downloads.pdfHost}
    </AdminPage>
  );
}

export default EmployeeRecord;
